import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, auth, postWithRetry } from "../lib/api";
import { toast } from "sonner";
import BrandHeader from "../components/BrandHeader";
import SessionFormBody, { Card } from "../components/SessionFormBody";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { CheckCircle2, Loader2, Paperclip, ImageIcon, X, ArrowLeft } from "lucide-react";
import {
  emptyForm, isFormComplete, toApiPayload,
} from "../lib/sessionForm";

const MAX_PHOTO_BYTES = 10 * 1024 * 1024; // 10 MB

export default function AdminPaperSession() {
  const nav = useNavigate();
  const fileRef = useRef(null);
  const [schools, setSchools] = useState([]);
  const [form, setForm] = useState(emptyForm());
  const [note, setNote] = useState("");
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  useEffect(() => {
    if (!auth.isAuthed()) {
      nav("/admin/login");
      return;
    }
    api.get("/schools").then((r) => setSchools(r.data)).catch(() => setSchools([]));
  }, [nav]);

  useEffect(() => {
    if (!photo) { setPhotoPreview(null); return; }
    const url = URL.createObjectURL(photo);
    setPhotoPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [photo]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setScore = (k, v) => setForm((f) => ({ ...f, scores: { ...f.scores, [k]: v } }));

  const canSave = useMemo(() => isFormComplete(form) && !saving, [form, saving]);

  const onPickPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > MAX_PHOTO_BYTES) {
      toast.error("Photo must be under 10 MB.");
      return;
    }
    if (!/^image\//.test(f.type)) {
      toast.error("Please attach an image file (JPG/PNG/HEIC).");
      return;
    }
    setPhoto(f);
  };

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    const RETRY_TOAST_ID = "paper-save-retry-status";
    try {
      const payload = { ...toApiPayload(form), transcribed_note: note.trim() };
      const res = await postWithRetry("/admin/paper-sessions", payload, {
        onRetry: (attempt) => {
          toast.loading(
            attempt === 1
              ? "Server is waking up — hanging on…"
              : `Still waking up — retry ${attempt + 1} of 3…`,
            { id: RETRY_TOAST_ID, duration: 15000 },
          );
        },
      });
      toast.dismiss(RETRY_TOAST_ID);
      const sessionId = res.data.id;

      let photoOk = true;
      if (photo) {
        const fd = new FormData();
        fd.append("file", photo);
        try {
          await postWithRetry(`/admin/sessions/${sessionId}/attach-photo`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
          });
        } catch (photoErr) {
          photoOk = false;
          toast.error("Session saved, but photo upload failed. You can retry attaching a photo shortly.");
        }
      }

      setLastResult({ ...res.data, teacher_name: payload.teacher_name, photoAttached: photo && photoOk });
      setForm(emptyForm());
      setNote("");
      setPhoto(null);
      if (fileRef.current) fileRef.current.value = "";
      window.scrollTo({ top: 0, behavior: "smooth" });
      toast.success("Paper session transcribed");
    } catch (err) {
      toast.dismiss(RETRY_TOAST_ID);
      const status = err?.response?.status;
      const isNetwork = !err.response;
      const isServer = typeof status === "number" && status >= 500;
      let msg;
      let desc;
      if (isNetwork || isServer) {
        msg = "Server didn't respond after 3 tries";
        desc = "This can happen briefly when the server is waking up. Your entries are still here — tap Save again in ~30 seconds.";
      } else {
        msg = err?.response?.data?.detail || "Couldn't save — please check your entries and try again.";
        desc = "Your entries have not been cleared.";
      }
      toast.error(msg, { duration: 10000, description: desc });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2] pb-32">
      <BrandHeader
        right={
          <Link to="/admin" className="text-xs text-neutral-500 hover:text-neutral-800 inline-flex items-center gap-1" data-testid="back-to-admin">
            <ArrowLeft className="w-3 h-3" /> Admin
          </Link>
        }
      />

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        <div>
          <h1 className="font-display text-3xl font-semibold text-neutral-900 tracking-tight">
            Log a paper session
          </h1>
          <p className="text-sm text-neutral-500 mt-1 max-w-2xl">
            Transcribe a physical CPI<span className="text-[9px] align-super">™</span> logsheet — same fields as the teacher form, plus an optional photo of the paper original as backup evidence. Saved sessions flow into reporting and exports like any other.
          </p>
        </div>

        {lastResult && (
          <div
            data-testid="paper-save-success"
            className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 flex items-start gap-3"
          >
            <CheckCircle2 className="w-5 h-5 text-emerald-700 mt-0.5 shrink-0" />
            <div className="text-sm text-emerald-900">
              <div className="font-semibold">Paper session transcribed & saved.</div>
              <div className="text-emerald-800 mt-0.5">
                Engagement <b>{lastResult.engagement_score}/25</b>{" · "}
                Behaviour <b>{lastResult.behaviour_score}/25</b>{" · "}
                Collective Energy <b>{lastResult.collective_energy_score}/25</b>
                {lastResult.cpi_total !== null && lastResult.cpi_total !== undefined ? (
                  <> {" · "}<b>CPI Total {lastResult.cpi_total}/100</b></>
                ) : (
                  <> {" · "}<b>CPI Total: N/A (Invalid Attendance)</b></>
                )}
                {lastResult.photoAttached && <> {" · "}Photo attached ✓</>}
              </div>
            </div>
          </div>
        )}

        {/* ---- Photo attachment ---- */}
        <Card
          title="Photo of the paper logsheet"
          hint="Optional but recommended — keeps the original as verifiable evidence alongside the transcription."
        >
          {!photoPreview ? (
            <label
              htmlFor="paper-photo"
              className="flex items-center gap-3 border-2 border-dashed border-neutral-300 rounded-lg p-4 cursor-pointer hover:border-neutral-500 transition-colors"
              data-testid="photo-drop"
            >
              <div className="w-12 h-12 rounded-lg bg-neutral-100 flex items-center justify-center shrink-0">
                <ImageIcon className="w-5 h-5 text-neutral-500" />
              </div>
              <div className="text-sm text-neutral-700 flex-1">
                <div className="font-medium">Attach a photo of the paper logsheet</div>
                <div className="text-neutral-500 text-xs mt-0.5">
                  JPG / PNG / HEIC, up to 10 MB.
                </div>
              </div>
              <div className="text-xs font-medium text-neutral-700 border border-neutral-300 rounded-full px-3 py-1.5 shrink-0">
                <Paperclip className="w-3.5 h-3.5 inline mr-1" /> Choose file
              </div>
              <input
                id="paper-photo"
                ref={fileRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={onPickPhoto}
                data-testid="input-photo"
              />
            </label>
          ) : (
            <div className="flex items-start gap-3">
              <img
                src={photoPreview}
                alt="Paper logsheet preview"
                className="w-32 h-32 object-cover rounded-lg border border-neutral-300"
                data-testid="photo-preview"
              />
              <div className="flex-1 text-sm text-neutral-700">
                <div className="font-medium">{photo?.name}</div>
                <div className="text-neutral-500 text-xs mt-0.5">
                  {(photo?.size / 1024 / 1024).toFixed(2)} MB
                </div>
                <button
                  onClick={() => { setPhoto(null); if (fileRef.current) fileRef.current.value = ""; }}
                  className="mt-2 text-xs text-red-700 hover:text-red-800 inline-flex items-center gap-1"
                  data-testid="btn-remove-photo"
                >
                  <X className="w-3 h-3" /> Remove
                </button>
              </div>
            </div>
          )}
        </Card>

        {/* ---- Shared form body ---- */}
        <SessionFormBody
          form={form}
          setField={setField}
          setScore={setScore}
          schools={schools}
        />

        {/* ---- Admin transcription note ---- */}
        <Card title="Transcription note (admin only)" hint="Optional — e.g. paper form dated, initials of who filled it, etc.">
          <Textarea
            data-testid="field-transcribed-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Transcribed from Ali's paper log dated 12 Feb — original scanned and attached."
            rows={3}
            className="resize-none"
          />
        </Card>
      </main>

      {/* Sticky save bar */}
      <div className="fixed bottom-0 inset-x-0 border-t border-neutral-200 bg-white/95 backdrop-blur z-40">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          <Button
            variant="outline"
            onClick={() => nav("/admin")}
            data-testid="btn-cancel-paper"
            className="h-11"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!canSave}
            data-testid="btn-save-paper-session"
            className="h-11 flex-1 bg-[#2D5A4F] hover:bg-[#264d43] text-white"
          >
            {saving ? (
              <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Saving…</>
            ) : (
              "Save transcribed session"
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
